import React, { FC, useState } from "react";
import {
  AccruedFee,
  AccruedTime,
  formatPrice,
  formatTime,
  GpmType,
  GpmThreshold,
} from "../../data-types";
import { Typography, Form, Table } from "antd";
import { PriceInput, PercentageInput } from "../input-number";
import { GpmOrSlider } from "../gpm-slider";
import { useGpmSettingsCheck } from "~/hooks";
import styles from "./matter-details-modal.css";
import { useTaxonomyLabel } from "~/context/taxonomy-context";

const {
  ["tab-content"]: tabContentClassName,
  ["tab-content-columns"]: tabContentColumnsClassName,
  ["left-column"]: leftColumnClassName,
  ["right-column"]: rightColumnClassName,
  ["matter-summary"]: matterSummaryClassName,
  ["form-container"]: formContainerClassName,
  ["form-row"]: formRowClassName,
  ["form-item"]: formItemClassName,
  ["table"]: tableClassName,
  ["table-cell"]: tableCellClassName,
  ["gpm-paragraph"]: gpmParagraphClassName,
} = styles as any;

export type WriteOffProjectionTabProps = {
  accruedFee: AccruedFee;
  currencySymbol: string;
  gpmThreshold?: GpmThreshold;
};

export const WriteOffProjectionTab: FC<WriteOffProjectionTabProps> = ({
  accruedFee,
  currencySymbol,
  gpmThreshold,
}) => {
  const taxonomy = useTaxonomyLabel();

  const { showGpm, showGpmSlider } = useGpmSettingsCheck();

  const [discountAmount, setDiscountAmount] = useState<number>(0);
  const [writeOffAmounts, setWriteOffAmounts] = useState<Map<number, number>>(new Map());

  const updateWriteOffAmount = (feeEarnerId: number, amount: number) => {
    setWriteOffAmounts(
      new Map(
        writeOffAmounts.set(
          feeEarnerId,
          Number(
            Math.min(
              amount,
              accruedFee.accruedTimes.find((t) => t.feeEarnerId === feeEarnerId)
                ?.actualFeeSinceLastInvoice ?? amount
            ).toFixed(2)
          )
        )
      )
    );
  };

  const totalAccruedFee = accruedFee.accruedTimes.reduce((acc, curr) => acc + curr.actualFee, 0);
  const unbilledWip = totalAccruedFee - accruedFee.amountBilled;
  const accruedFeeSinceLastInvoice = accruedFee.accruedTimes.reduce(
    (acc, curr) => acc + curr.actualFeeSinceLastInvoice,
    0
  );
  const priorWriteOffs = unbilledWip - accruedFeeSinceLastInvoice; // accrued fees from before the last invoice that have not been billed, assumed to be written off

  const updateDiscountAmount = (amount: number) => {
    const amountOrMax = Number(Math.min(amount, accruedFeeSinceLastInvoice).toFixed(2));
    setDiscountAmount(amountOrMax);
    setWriteOffAmounts(
      new Map<number, number>(
        Array.from(accruedFee.accruedTimes).map((record) => [
          record.feeEarnerId,
          Number(
            (amountOrMax * (record.actualFeeSinceLastInvoice / accruedFeeSinceLastInvoice)).toFixed(
              2
            )
          ),
        ])
      )
    );
  };

  const currentWriteOffs =
    Array.from(writeOffAmounts.values()).reduce((acc, curr) => acc + curr, 0) || 0;
  const sumMatterWriteOffs = priorWriteOffs + currentWriteOffs;
  const sumCurrentCosts = accruedFee.accruedTimes.reduce(
    (acc, curr) => acc + curr.actualCostSinceLastInvoice,
    0
  );
  const sumMatterCosts = accruedFee.accruedTimes.reduce((acc, curr) => acc + curr.actualCost, 0);

  const anyTimeRecordedSinceLastInvoice =
    accruedFee.accruedTimes.filter((t) => t.actualFeeSinceLastInvoice > 0).length > 0;

  return (
    <div className={tabContentClassName}>
      <div className={tabContentColumnsClassName}>
        <div className={leftColumnClassName}>
          <Typography.Paragraph>
            Unbilled work-in-progress: {formatPrice(unbilledWip, accruedFee.currencySymbol)}
          </Typography.Paragraph>
          <Typography.Paragraph>
            Accrued billable time since last invoice:{" "}
            {formatPrice(accruedFeeSinceLastInvoice, accruedFee.currencySymbol)}
          </Typography.Paragraph>

          <div className={formContainerClassName}>
            {/* <Form className={formClassName}> */}
            <div className={formRowClassName}>
              <Form.Item
                label={`Pro rata discount over all ${taxonomy.FeeEarner}s`}
                className={formItemClassName}
              >
                <PriceInput
                  currencySymbol={currencySymbol}
                  step={100}
                  value={discountAmount}
                  onChange={(value) => updateDiscountAmount(value)}
                  style={{ width: "120px" }}
                />
              </Form.Item>
              <Form.Item className={formItemClassName}>
                <PercentageInput
                  value={Math.round((100 * discountAmount) / accruedFeeSinceLastInvoice) || 0}
                  onChange={(value) =>
                    updateDiscountAmount(
                      Number(((value * accruedFeeSinceLastInvoice) / 100).toFixed(2))
                    )
                  }
                  decimalPlaces={0}
                />
              </Form.Item>
            </div>
          </div>
        </div>
        <div className={rightColumnClassName}>
          <div className={matterSummaryClassName}>
            <h4>Matter Summary</h4>
            <Typography.Paragraph>
              Total write-off value: {formatPrice(sumMatterWriteOffs, accruedFee.currencySymbol)}
            </Typography.Paragraph>
            <Typography.Paragraph className={gpmParagraphClassName}>
              <span style={{ marginRight: "10px" }}>GPM before write-offs:</span>
              {totalAccruedFee > 0 ? (
                <GpmOrSlider
                  isFeeEarnerLevelGpm={false}
                  gpm={{
                    fixed: Math.round(((totalAccruedFee - sumMatterCosts) / totalAccruedFee) * 100),
                    type: GpmType.Fixed,
                  }}
                  gpmThreshold={gpmThreshold ?? null}
                  showRequired={true}
                  showGpm={showGpm}
                  showGpmSlider={showGpmSlider}
                />
              ) : (
                <Typography.Text>N/A</Typography.Text>
              )}
            </Typography.Paragraph>
            <Typography.Paragraph className={gpmParagraphClassName}>
              <span style={{ marginRight: "10px" }}>GPM after write-offs:</span>
              {totalAccruedFee > 0 ? (
                <GpmOrSlider
                  isFeeEarnerLevelGpm={false}
                  gpm={{
                    fixed: Math.round(
                      ((totalAccruedFee - sumMatterCosts - sumMatterWriteOffs) / totalAccruedFee) *
                        100
                    ),
                    type: GpmType.Fixed,
                  }}
                  gpmThreshold={gpmThreshold ?? null}
                  showRequired={true}
                  showGpm={showGpm}
                  showGpmSlider={showGpmSlider}
                />
              ) : (
                <Typography.Text>N/A</Typography.Text>
              )}
            </Typography.Paragraph>
          </div>
        </div>
      </div>
      {anyTimeRecordedSinceLastInvoice ? (
        <div className={tableClassName}>
          <Table
            dataSource={accruedFee.accruedTimes.filter((t) => t.actualFeeSinceLastInvoice > 0)}
            columns={[
              {
                title: taxonomy.FeeEarnerLevel,
                dataIndex: "feeEarnerLevelDescription",
                key: "feeEarnerLevelDescription",
              },
              {
                title: `${taxonomy.FeeEarner}s`,
                dataIndex: "feeEarnerName",
                key: "feeEarnerName",
              },
              {
                title: "Time Recorded Since Last Invoice (hrs:min)",
                dataIndex: "actualDurationSinceLastInvoice",
                key: "actualDurationSinceLastInvoice",
                render: (actualDurationSinceLastInvoice: number) =>
                  formatTime(actualDurationSinceLastInvoice),
                align: "right",
                width: "15%",
              },
              {
                title: "Accrued Fee Since Last Invoice",
                dataIndex: "actualFeeSinceLastInvoice",
                key: "actualFeeSinceLastInvoice",
                render: (actualFee: number) => formatPrice(actualFee, currencySymbol),
                align: "right",
                width: "15%",
              },
              {
                title: "Write-off Amount",
                key: `writeOffAmount`,
                render: (record: AccruedTime) => {
                  return (
                    <div className={tableCellClassName}>
                      <PriceInput
                        currencySymbol={currencySymbol}
                        step={100}
                        value={writeOffAmounts.get(record.feeEarnerId) ?? 0}
                        onChange={(value) => updateWriteOffAmount(record.feeEarnerId, value)}
                        style={{ width: "120px" }}
                      />
                    </div>
                  );
                },
                align: "right",
                width: "100px",
              },
              {
                title: "Write Off %",
                key: "writeOffPercentage",
                render: (record: AccruedTime) => {
                  return (
                    <div className={tableCellClassName}>
                      <PercentageInput
                        value={Math.round(
                          (100 * (writeOffAmounts.get(record.feeEarnerId) ?? 0)) /
                            record.actualFeeSinceLastInvoice
                        )}
                        onChange={(value) => {
                          updateWriteOffAmount(
                            record.feeEarnerId,
                            (value * record.actualFeeSinceLastInvoice) / 100
                          );
                        }}
                        decimalPlaces={0}
                      />
                    </div>
                  );
                },
                align: "right",
                width: "100px",
              },
              {
                title: "GPM",
                key: "gpm",
                render: (record: AccruedTime) => {
                  const newFee =
                    record.actualFeeSinceLastInvoice -
                    (writeOffAmounts.get(record.feeEarnerId) ?? 0);
                  const gpm = Math.round(
                    ((newFee - record.actualCostSinceLastInvoice) / newFee) * 100
                  );
                  return (
                    <div className={tableCellClassName}>
                      <GpmOrSlider
                        isFeeEarnerLevelGpm={false}
                        gpm={{
                          fixed: gpm,
                          type: GpmType.Fixed,
                        }}
                        gpmThreshold={gpmThreshold ?? null}
                        showRequired={false}
                        showGpm={showGpm}
                        showGpmSlider={showGpmSlider}
                      />
                    </div>
                  );
                },
                align: "center",
                width: "10%",
              },
            ]}
            summary={() => {
              return (
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0}>Totals</Table.Summary.Cell>
                  <Table.Summary.Cell index={1}></Table.Summary.Cell>
                  <Table.Summary.Cell index={2}></Table.Summary.Cell>
                  <Table.Summary.Cell index={3} align="right">
                    {formatPrice(accruedFeeSinceLastInvoice, accruedFee.currencySymbol)}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={4} align="right">
                    {formatPrice(currentWriteOffs, currencySymbol)}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={5} align="right">
                    {Math.round((100 * currentWriteOffs) / accruedFeeSinceLastInvoice)}%
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={6}>
                    <div className={tableCellClassName}>
                      <GpmOrSlider
                        isFeeEarnerLevelGpm={false}
                        gpm={{
                          fixed: Math.round(
                            ((accruedFeeSinceLastInvoice - currentWriteOffs - sumCurrentCosts) /
                              accruedFeeSinceLastInvoice) *
                              100
                          ),
                          type: GpmType.Fixed,
                        }}
                        gpmThreshold={gpmThreshold ?? null}
                        showRequired={false}
                        showGpm={showGpm}
                        showGpmSlider={showGpmSlider}
                      />
                    </div>
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              );
            }}
          />
        </div>
      ) : (
        <Typography.Paragraph style={{ color: "red" }}>
          No time recorded since last invoice.
        </Typography.Paragraph>
      )}
    </div>
  );
};
